import React, { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { FormikProvider, useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import AfterMintItemModal from "../AfterMintItemModal";

import Box from "@mui/material/Box";
import { Typography } from "@mui/material";

import { physicalItemSchema } from "validators/client/physicalItemOnchain";

import { selectAddress, selectUser } from "redux/slices/metamask";

import MintItemModal from "sections/itemsPage/MintItemModal";

import Connectors from "components/Connectors";
import Divider from "components/Divider";
import { INFO_BOX_TYPE } from "components/InfoBox/types";
import InfoBox from "components/InfoBox";
import Button from "components/CustomButton";
import CustomOutlinedInput from "components/CustomOutlinedInput";

import useLogger from "hooks/useLogger";

import { STORAGE } from "enums/storage";
import { ACTION_TYPES } from "enums/logger/ActionTypes";
import { PATHS, PHYSICAL_TO_NFT_STEPS } from "enums/logger/Paths";
import { NFC_MINT_TYPES } from "enums/nfcMintTypes";

import { getObjectItem } from "utils/storage";
import { formatAccount } from "utils/blockchain";

interface MintItemToWalletFormProps {
  data: {
    hash: string;
    contractAddress: string;
    nfcContractAddress?: string | null;
    tokenId: number;
    itemName: string;
  };
  isCards: boolean;
  canMint: boolean;
  disabled?: boolean;
  isPinCodeRequired?: boolean;
  nicknameLabel?: string;
  buttonText?: string;
}

const MintItemToWalletForm: FC<MintItemToWalletFormProps> = ({
  data,
  disabled = false,
  isCards,
  canMint,
  isPinCodeRequired = false,
  buttonText,
}) => {
  const user = useSelector(selectUser);
  const metamaskAddress = useSelector(selectAddress);

  const [isMintModalOpen, setMintModalOpen] = useState(false);
  const [isAfterMintModalOpen, setAfterMintModalOpen] = useState(false);
  const log = useLogger();
  const storageData = getObjectItem<{ nickname: string; email: string }>(
    STORAGE.FORM_DATA,
  );

  const handleSubmit = useCallback(async () => {
    if (!canMint) {
      return;
    }
    setMintModalOpen(true);
    log({
      path: PATHS.PHYSICAL_TO_NFT,
      step: PHYSICAL_TO_NFT_STEPS.OPEN_MODAL,
      data,
      actionType: ACTION_TYPES.USER,
    });
  }, [log, data, canMint]);

  const handleMintModalClose = useCallback(() => {
    setMintModalOpen(false);
    log({
      path: PATHS.PHYSICAL_TO_NFT,
      step: null,
      data,
      actionType: ACTION_TYPES.USER,
    });
  }, [log, data]);

  const handleMinted = useCallback(() => {
    handleMintModalClose();
    setAfterMintModalOpen(true);
  }, [handleMintModalClose]);

  const formik = useFormik({
    initialValues: {
      nickname: user?.nickname ?? storageData?.nickname ?? "",
      walletAddress: metamaskAddress ?? "",
    },
    validationSchema: toFormikValidationSchema(physicalItemSchema),
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <Box>
      <FormikProvider value={formik}>
        {/* If user has both required variables, display a quick mint form */}
        {metamaskAddress && user.nickname && (
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Typography variant="h6">
                Welcome back, <b>{user.nickname}</b>!
              </Typography>
              <Typography
                fontSize={12}
                sx={{
                  opacity: 0.75,
                }}
                paragraph
              >
                Wallet ending with {formatAccount(metamaskAddress)}
              </Typography>
              <Typography paragraph>
                Press 'Claim' and we'll send your item straight to your personal
                wallet.
              </Typography>
              <Button
                type="submit"
                isLoading={isMintModalOpen}
                disabled={disabled || isMintModalOpen || !canMint}
                sx={{ minHeight: 44 }}
              >
                {buttonText || "CLAIM NFT"}
              </Button>
            </form>
          </Box>
        )}
        {/* If user is missing either of the required variables, display the standard form. Prefill what's known. */}
        {(!user.nickname || !metamaskAddress) && (
          <>
            <Typography variant="h6" mb={2}>
              Claim your NFT!
            </Typography>
            <Typography paragraph>
              Once you claim your NFT, it goes directly to your own wallet,
              ready for you to access or transfer at any time.
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <CustomOutlinedInput
                label="Nickname*"
                inputProps={{
                  id: "nickname",
                  placeholder: "Nickname",
                  value: formik.values.nickname,
                  onChange: formik.handleChange,
                }}
                formControlProps={{
                  fullWidth: true,
                  margin: "normal",
                }}
                error={formik.errors.nickname}
              />
              <CustomOutlinedInput
                label="Wallet address/ENS*"
                inputProps={{
                  id: "walletAddress",
                  placeholder: "Wallet address/ENS",
                  value: formik.values.walletAddress,
                  onChange: formik.handleChange,
                }}
                formControlProps={{
                  fullWidth: true,
                  margin: "normal",
                }}
                error={formik.errors.walletAddress}
              />
              {!canMint && (
                <InfoBox type={INFO_BOX_TYPE.INFO}>
                  Mint is only available after scanning the NFC pin from the
                  physical item
                </InfoBox>
              )}
              <Button
                type="submit"
                isLoading={isMintModalOpen}
                disabled={disabled || isMintModalOpen || !canMint}
                sx={{ minHeight: 44 }}
              >
                {buttonText || "CLAIM NFT"}
              </Button>
            </form>
            <Divider />
            <Typography variant="h6" mb={2}>
              Quick fill
            </Typography>
            <Typography paragraph>
              Connect your wallet, and we'll pre-fill your address!
            </Typography>
            <Connectors />
          </>
        )}
        {isMintModalOpen && (
          <MintItemModal
            data={data}
            isPinCodeRequired={isPinCodeRequired}
            isOpen={isMintModalOpen}
            additionalData={formik.values}
            onClose={handleMintModalClose}
            onAfterMint={handleMinted}
            isCards={isCards}
            mintType={NFC_MINT_TYPES.ONCHAIN}
          />
        )}
        {isAfterMintModalOpen && (
          <AfterMintItemModal
            isOpen={isAfterMintModalOpen}
            itemName={data.itemName}
          />
        )}
      </FormikProvider>
    </Box>
  );
};

export default MintItemToWalletForm;
